import { createTheme, alpha } from '@mui/material/styles';

// ─────────────────────────────────────────────────────────────────────────────
// RAW COLOR PALETTE
// Single source of truth for every hex value used across the app.
// ─────────────────────────────────────────────────────────────────────────────
export const colors = {
  // Brand / primary
  purple: {
    main: '#667eea',
    dark: '#764ba2',
    light: '#8b9cf5',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },

  // Strategy type palettes
  flowLeaders:         { main: '#667eea', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  qualityGrowth:       { main: '#10b981', dark: '#059669', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
  valueConfirmation:   { main: '#3b82f6', dark: '#2563eb', gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' },
  turnaround:          { main: '#f59e0b', dark: '#d97706', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' },
  accumulationBreakout:{ main: '#8b5cf6', dark: '#7c3aed', gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' },
  reversal:            { main: '#ef4444', dark: '#dc2626', gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' },
  superStock:          { main: '#06b6d4', dark: '#0891b2', gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' },
  priceAction:         { main: '#e97320', dark: '#c2590a', gradient: 'linear-gradient(135deg, #e97320 0%, #c2590a 100%)' },

  // Semantic status
  success: { main: '#10b981', light: '#6ee7b7', dark: '#059669' },
  warning: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706' },
  error:   { main: '#ef4444', light: '#f87171', dark: '#dc2626' },
  info:    { main: '#3b82f6', light: '#60a5fa', dark: '#2563eb' },

  // Neutral gray scale (Tailwind slate)
  gray: {
    50:  '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },

  background: {
    default: '#f8fafc',
    paper:   '#ffffff',
    card:    '#ffffff',
    hover:   '#f7fafc',
  },

  text: {
    primary:   '#0f172a',
    secondary: '#64748b',
    disabled:  '#94a3b8',
  },

  chart: {
    positive: '#10b981',
    negative: '#ef4444',
    neutral:  '#64748b',
  },

  // Market convention: up / down / unchanged
  stock: {
    positive:  '#10b981',
    negative:  '#ef4444',
    reference: '#f59e0b',
  },

  // Accent colors — generic hue tokens reusable across features
  accent: {
    green:  '#22c55e',  // vibrant green
    cyan:   '#0891b2',  // cyan dark
    violet: '#a855f7',  // violet
    sky:    '#0ea5e9',  // sky blue
    orange: '#f97316',  // orange
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// SEMANTIC STATUS TOKENS
// Each status exposes { main, bg, border, text } for consistent chip/badge use.
// ─────────────────────────────────────────────────────────────────────────────
export const status = {
  positive: { main: '#10b981', bg: '#ecfdf5', border: '#a7f3d0', text: '#059669' },
  negative: { main: '#ef4444', bg: '#fef2f2', border: '#fecaca', text: '#dc2626' },
  warning:  { main: '#f59e0b', bg: '#fffbeb', border: '#fde68a', text: '#d97706' },
  neutral:  { main: '#64748b', bg: '#f8fafc', border: '#e2e8f0', text: '#475569' },
  info:     { main: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', text: '#2563eb' },
  purple:   { main: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe', text: '#7c3aed' },
  cyan:     { main: '#06b6d4', bg: '#ecfeff', border: '#a5f3fc', text: '#0891b2' },
};

// ─────────────────────────────────────────────────────────────────────────────
// MARKET REGIME COLORS
// Used across 28+ pages for RISK_ON / RISK_OFF / NEUTRAL regime chips.
// ─────────────────────────────────────────────────────────────────────────────
export const regime = {
  RISK_ON:  { label: 'Risk On',  ...status.positive },
  RISK_OFF: { label: 'Risk Off', ...status.negative },
  NEUTRAL:  { label: 'Neutral',  ...status.warning },
  RECOVERY: { label: 'Recovery', ...status.info },
  SIDEWAY:  { label: 'Sideway',  ...status.warning },
  // Market filter labels
  ALLOW:    { label: 'Allow',    ...status.positive },
  WATCH:    { label: 'Watch',    ...status.warning },
  AVOID:    { label: 'Avoid',    ...status.negative },
};

/** Returns the regime config for a given regime key, defaulting to NEUTRAL. */
export const getRegimeConfig = (key) => regime[key] || regime.NEUTRAL;

// ─────────────────────────────────────────────────────────────────────────────
// SIGNAL / ALERT LEVEL COLORS
// ─────────────────────────────────────────────────────────────────────────────
export const alertLevel = {
  GREEN:    { color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0', weight: 0 },
  GUARD:    { color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', weight: 1 },
  YELLOW:   { color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', weight: 2 },
  RED:      { color: '#ef4444', bg: '#fef2f2', border: '#fecaca', weight: 3 },
  CRITICAL: { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', weight: 4 },
};

export const getAlertConfig = (key) => alertLevel[key?.toUpperCase()] || alertLevel.YELLOW;

// ─────────────────────────────────────────────────────────────────────────────
// PRIORITY CONFIGS  (A+ / A / B+ / B)
// Used in MarketDivergence, EarlyDivergenceWatchlist, etc.
// ─────────────────────────────────────────────────────────────────────────────
export const priority = {
  'A+': { color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0', i18nKey: 'marketDivergence.priority.buy'   },
  'A':  { color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', i18nKey: 'marketDivergence.priority.watch' },
  'B+': { color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', i18nKey: 'marketDivergence.priority.wait'  },
  'B':  { color: '#94a3b8', bg: '#f8fafc', border: '#e2e8f0', i18nKey: 'marketDivergence.priority.skip'  },
};

export const getPriorityConfig = (key) => priority[key] || priority['B'];

// ─────────────────────────────────────────────────────────────────────────────
// SCORE COLOR MAP
// UI-only concern: maps backend-supplied level keys → display colors.
// Threshold logic (when a score becomes "excellent") lives in the backend.
// ─────────────────────────────────────────────────────────────────────────────
export const scoreColors = {
  unknown:   { bg: '#94a3b8', text: '#fff' },
  excellent: { bg: '#10b981', text: '#fff' },
  veryGood:  { bg: '#3b82f6', text: '#fff' },
  good:      { bg: '#f59e0b', text: '#fff' },
  fair:      { bg: '#8b5cf6', text: '#fff' },
  poor:      { bg: '#ef4444', text: '#fff' },
};

/** Resolve a backend-supplied score level key → fill color. */
export const getScoreColor  = (levelKey) => (scoreColors[levelKey ?? 'unknown'] ?? scoreColors.unknown).bg;

/** Resolve a backend-supplied score level key → semi-transparent background. */
export const getScoreBg     = (levelKey) => alpha(getScoreColor(levelKey), 0.15);

// ─────────────────────────────────────────────────────────────────────────────
// FONT SIZE SCALE
// Named sizes matching the most common values found across all pages.
// ─────────────────────────────────────────────────────────────────────────────
export const fontSize = {
  xxs:  '0.55rem',  // micro labels
  xs:   '0.62rem',  // tiny chip labels
  sm:   '0.68rem',  // small labels / table cells
  md:   '0.75rem',  // default body small
  base: '0.82rem',  // base value display
  lg:   '0.9rem',   // slightly larger labels
  xl:   '1rem',     // standard body
  '2xl':'1.125rem',
  '3xl':'1.25rem',
};

// ─────────────────────────────────────────────────────────────────────────────
// REUSABLE SX PRESETS
// Drop these directly into sx={{ ...sxPresets.card }} to stay consistent.
// ─────────────────────────────────────────────────────────────────────────────
export const sxPresets = {
  /** Standard white card with border */
  card: {
    bgcolor: '#ffffff',
    borderRadius: 2,
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
  },

  /** Card with subtle hover elevation */
  cardHover: {
    bgcolor: '#ffffff',
    borderRadius: 2,
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    transition: 'box-shadow 0.2s, border-color 0.2s',
    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)', borderColor: '#cbd5e1' },
  },

  /** Page-level section card */
  sectionCard: {
    bgcolor: '#ffffff',
    borderRadius: 2,
    border: '1px solid #f1f5f9',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    p: 2,
  },

  /** Table header cell */
  tableHead: {
    fontWeight: 700,
    fontSize: '0.72rem',
    color: '#64748b',
    bgcolor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    py: 1,
  },

  /** Metric label (left side of a label/value pair) */
  metricLabel: {
    fontSize: '0.72rem',
    color: '#64748b',
    fontWeight: 600,
    lineHeight: 1.3,
  },

  /** Metric value (right side of a label/value pair, monospace) */
  metricValue: {
    fontSize: '0.82rem',
    fontWeight: 700,
    fontFamily: 'monospace',
    color: '#1e293b',
  },

  /** Small compact Chip-like inline badge */
  chip: {
    height: 20,
    fontSize: '0.62rem',
    fontWeight: 700,
    borderRadius: 1,
  },

  /** Standard small Chip */
  chipSm: {
    height: 22,
    fontSize: '0.68rem',
    fontWeight: 700,
    borderRadius: 1.5,
  },

  /** Section heading row */
  sectionTitle: {
    fontWeight: 800,
    fontSize: '0.82rem',
    color: '#1e293b',
    lineHeight: 1.3,
  },

  /** Muted sub-label */
  subLabel: {
    fontSize: '0.65rem',
    color: '#94a3b8',
    fontWeight: 500,
  },

  /** Divider line */
  divider: {
    borderTop: '1px solid #f1f5f9',
    my: 1,
  },

  /** Scrollable container */
  scrollY: {
    overflowY: 'auto',
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
    '&::-webkit-scrollbar-thumb': { bgcolor: '#e2e8f0', borderRadius: 2 },
  },

  /** Flex row center */
  row: { display: 'flex', alignItems: 'center' },

  /** Flex row space-between */
  rowBetween: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },

  /** Absolute fill */
  fill: { position: 'absolute', inset: 0 },

  /** Truncated single line text */
  truncate: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
};

// ─────────────────────────────────────────────────────────────────────────────
// STATUS CHIP HELPER
// Returns sx for a status-coloured chip/badge from the status map.
// Usage: sx={{ ...statusChipSx('positive') }}
// ─────────────────────────────────────────────────────────────────────────────
export const statusChipSx = (statusKey, opts = {}) => {
  const s = status[statusKey] || status.neutral;
  return {
    bgcolor: s.bg,
    color:   s.text,
    border: `1px solid ${alpha(s.main, 0.25)}`,
    fontWeight: 700,
    ...opts,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// TYPOGRAPHY SCALE
// ─────────────────────────────────────────────────────────────────────────────
export const typography = {
  fontFamily: 'Inter, Roboto, sans-serif',
  h1: { fontSize: '2.5rem',   fontWeight: 800, lineHeight: 1.2 },
  h2: { fontSize: '2rem',     fontWeight: 800, lineHeight: 1.2 },
  h3: { fontSize: '1.75rem',  fontWeight: 700, lineHeight: 1.3 },
  h4: { fontSize: '1.5rem',   fontWeight: 700, lineHeight: 1.3 },
  h5: { fontSize: '1.25rem',  fontWeight: 700, lineHeight: 1.4 },
  h6: { fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.4 },
  body1:   { fontSize: '1rem',     lineHeight: 1.5 },
  body2:   { fontSize: '0.875rem', lineHeight: 1.5 },
  caption: { fontSize: '0.75rem',  lineHeight: 1.4 },
};

// ─────────────────────────────────────────────────────────────────────────────
// SPACING / RADIUS / SHADOWS
// ─────────────────────────────────────────────────────────────────────────────
export const spacing = { xs: 4, sm: 8, md: 16, lg: 24, xl: 32, xxl: 48 };

export const borderRadius = { sm: 4, md: 8, lg: 12, xl: 16, full: 9999 };

export const shadows = {
  xs: '0 1px 2px rgba(15,23,42,0.04)',
  sm: '0 1px 3px rgba(0,0,0,0.08)',
  md: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
  lg: '0 4px 12px rgba(0,0,0,0.08)',
  xl: '0 8px 24px rgba(0,0,0,0.12)',
  '2xl': '0 20px 40px rgba(0,0,0,0.15)',
};

// ─────────────────────────────────────────────────────────────────────────────
// MUI THEME
// ─────────────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary:    { main: colors.purple.main,         dark: colors.purple.dark,         light: colors.purple.light },
    secondary:  { main: colors.qualityGrowth.main,  dark: colors.qualityGrowth.dark },
    success:    { main: colors.success.main,         light: colors.success.light,      dark: colors.success.dark },
    warning:    { main: colors.warning.main,         light: colors.warning.light,      dark: colors.warning.dark },
    error:      { main: colors.error.main,           light: colors.error.light,        dark: colors.error.dark },
    info:       { main: colors.info.main,            light: colors.info.light,         dark: colors.info.dark },
    background: { default: colors.background.default, paper: colors.background.paper },
    text:       { primary: colors.text.primary, secondary: colors.text.secondary, disabled: colors.text.disabled },
    divider:    colors.gray[200],
  },
  typography: {
    fontFamily: typography.fontFamily,
    ...Object.fromEntries(['h1','h2','h3','h4','h5','h6','body1','body2','caption'].map(k => [k, typography[k]])),
  },
  shape: { borderRadius: borderRadius.md },
  shadows: ['none', shadows.xs, shadows.sm, shadows.sm, shadows.md, shadows.md,
    shadows.lg, shadows.lg, shadows.lg, shadows.xl, shadows.xl, shadows.xl,
    shadows['2xl'], shadows['2xl'], shadows['2xl'],
    ...Array(10).fill(shadows['2xl']),
  ],
  components: {
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: borderRadius.lg, boxShadow: shadows.md },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: borderRadius.md },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: borderRadius.md, textTransform: 'none', fontWeight: 600 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: colors.gray[200] },
        head: { fontWeight: 700, color: colors.text.primary, backgroundColor: colors.background.hover },
      },
    },
  },
});

export default theme;
